import type { CityConfig } from "@/pages/city-template/types";

export const milfordConfig: CityConfig = {
  city: "Milford",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "milford",
  route: "/cities/milford",
  title: "Expedited Passport Services in Milford, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Milford, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Milford, Orange, Woodbridge, Stratford, and all of New Haven County.",
  h1: "Expedited Passport Services in Milford, CT",
  heroSubtitle:
    "Milford travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No need to visit the New York Passport Agency.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Milford%20Connecticut%20coastline%20with%20the%20historic%20town%20green%20and%20Milford%20Harbor%20at%20golden%20hour%2C%20charming%20New%20England%20coastal%20town%20with%20sailboats%20on%20Long%20Island%20Sound%2C%20warm%20amber%20lighting%2C%20colonial%20architecture%20and%20sandy%20beaches%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-mil-hero-001&orientation=landscape",
  heroImageAlt: "Milford Connecticut harbor and town green at golden hour",
  geoPosition: "41.2223; -73.0565",
  geoPlacename: "Milford, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~75 miles from Milford",
  },
  nearbyCitiesZips: [
    { name: "Milford", zips: "06460, 06461" },
    { name: "Orange", zips: "06477" },
    { name: "Woodbridge", zips: "06525" },
    { name: "Derby", zips: "06418" },
    { name: "Ansonia", zips: "06401" },
    { name: "Seymour", zips: "06483" },
    { name: "Shelton", zips: "06484, 06485" },
    { name: "Stratford", zips: "06614, 06615" },
    { name: "Trumbull", zips: "06611" },
    { name: "Bridgeport", zips: "06601, 06602, 06604, 06605, 06606, 06607, 06608, 06610, 06650, 06673, 06699" },
    { name: "New Haven", zips: "06501-06512, 06515, 06516, 06517, 06519, 06520, 06524, 06525, 06530, 06531, 06532, 06533, 06536, 06537, 06538" },
    { name: "West Haven", zips: "06516" },
    { name: "Hamden", zips: "06514, 06517, 06518" },
    { name: "North Haven", zips: "06473" },
    { name: "Branford", zips: "06405" },
    { name: "Guilford", zips: "06437" },
    { name: "Madison", zips: "06443" },
    { name: "Clinton", zips: "06413" },
    { name: "Naugatuck", zips: "06770" },
    { name: "Beacon Falls", zips: "06403" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Milford travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving New Haven County",
      desc: "Milford, Orange, Stratford, Shelton, West Haven, and all New Haven County communities.",
    },
  ],
  testimonials: [
    {
      name: "Daniel W.",
      location: "Milford, CT",
      rating: 5,
      text: "I had a work trip to Canada in 4 days and my passport expired. Fast Passport Center had a new one in my hands in 3 days. Milford residents can absolutely trust them.",
      service: "Emergency Passport",
    },
    {
      name: "Carol M.",
      location: "Orange, CT",
      rating: 5,
      text: "Renewed my passport for a cruise to Bermuda. Fast Passport Center made it easy and had it back to Orange in 4 days. Great New Haven County service.",
      service: "Passport Renewal",
    },
    {
      name: "Brian T.",
      location: "Stratford, CT",
      rating: 5,
      text: "Applied for my son's first passport for a family trip to Europe. Fast Passport Center handled the child passport process and had it ready in 4 days. Stratford families trust them.",
      service: "Child Passport",
    },
    {
      name: "Laura K.",
      location: "Shelton, CT",
      rating: 5,
      text: "Needed a name change after marriage. Fast Passport Center handled the paperwork and I had my updated passport in 4 days. Excellent Shelton-area service.",
      service: "Name Change",
    },
  ],
  facilities: [
    {
      id: "mil-1",
      name: "Milford Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "64 New Haven Avenue",
      city: "Milford",
      state: "CT",
      zip: "06460",
      phone: "203-878-1578",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Milford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "mil-2",
      name: "Milford City Hall",
      type: "City Clerk / Passport Services",
      address: "110 River Street",
      city: "Milford",
      state: "CT",
      zip: "06460",
      phone: "203-783-3200",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Milford City Center",
      website: "https://www.milfordct.gov/",
      websiteLabel: "City of Milford",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "mil-3",
      name: "Orange Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "441 Boston Post Road",
      city: "Orange",
      state: "CT",
      zip: "06477",
      phone: "203-799-0721",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Orange",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "mil-4",
      name: "Stratford Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "20 Ferry Boulevard",
      city: "Stratford",
      state: "CT",
      zip: "06614",
      phone: "203-375-1441",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Stratford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "mil-5",
      name: "Shelton Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "425 Howe Avenue",
      city: "Shelton",
      state: "CT",
      zip: "06484",
      phone: "203-924-7441",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Shelton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "mil-6",
      name: "West Haven Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "300 Main Street",
      city: "West Haven",
      state: "CT",
      zip: "06516",
      phone: "203-934-0123",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "West Haven",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Milford, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in Milford?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Milford?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Milford, Orange, and Stratford. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Milford?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Milford?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Milford?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Milford. We serve Milford and all of New Haven County through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Milford for international travel?",
      a: "Tweed New Haven Airport (HVN) is the closest at ~8 miles, with limited service. JFK International is ~75 miles, LaGuardia is ~65 miles, and Bradley International (BDL) is ~55 miles. Many Milford residents use JFK or LaGuardia for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Milford?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Milford?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Milford?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Milford?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Milford have a local passport agency?",
      a: "No, Milford does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 75 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Milford", address: "various CVS locations in Milford", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Milford", address: "various Walgreens locations in Milford", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Milford", address: "various UPS Store locations in Milford", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Milford", address: "various FedEx Office locations in Milford", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Milford", address: "various Walmart locations in Milford", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Milford",
      address: "OwenJ Ireland Complex, 2138-R Dixwell Avenue, Hamden, CT 06514",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: true,
    },
    {
      name: "CT DMV — New Haven",
      address: "200 Orange Street, New Haven, CT 06510",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~75 miles from Milford",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "One of the busiest international airports in the world with flights to virtually every global destination. Many Milford residents use JFK for international travel.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: true,
    },
    {
      code: "LGA",
      name: "LaGuardia Airport",
      distance: "~65 miles from Milford",
      address: "Queens, NY 11371",
      note: "Major domestic airport with extensive U.S. flight coverage. Convenient for Milford travelers.",
      flightUrl: "https://www.laguardiaairport.com/",
      flightLabel: "LaGuardia Airport",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~55 miles from Milford",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's primary airport. Offers domestic flights and international connections to Canada, Ireland, and the Caribbean.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Milford travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Milford residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Milford residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Milford residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Milford, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Milford residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Milford travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Milford residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Milford residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Milford residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Milford." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Milford." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Milford travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Milford travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our New Haven County team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Milford address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Milford", route: "/cities/milford" },
    { name: "New Haven", route: "/cities/new-haven" },
    { name: "Stamford", route: "/cities/stamford" },
    { name: "Bridgeport", route: "/cities/bridgeport" },
    { name: "Waterbury", route: "/cities/waterbury" },
    { name: "Norwalk", route: "/cities/norwalk" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Milford and all of New Haven County as a remote courier service. We do not have a physical office in Milford, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Remote courier service — Milford, CT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Milford neighborhoods and surrounding New Haven County communities. From Milford Center to Devon, Orange to Stratford — we serve every New Haven County community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Milford and all surrounding New Haven County communities. Whether you're in downtown Milford or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Milford customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Milford travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Milford and New Haven County travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Milford, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
  dmvWebsite: "https://portal.ct.gov/DMV",
  dmvLabel: "Connecticut DMV — Official Website",
};

export default milfordConfig;